import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { 
  Plus, Pencil, Trash, Calendar, MapPin, Users, Award, 
  Clock, Building, Grid, List, Upload, X, Image, Globe, Settings, Copy
} from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import ZoneConfigModal from '../competition/ZoneConfigModal';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Common timezones for competition events
const TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Europe/Rome (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (CET/CEST)' },
  { value: 'Europe/Athens', label: 'Europe/Athens (EET/EEST)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST/NZDT)' },
  { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT)' },
  { value: 'America/Mexico_City', label: 'America/Mexico_City (CST/CDT)' },
  { value: 'Africa/Cairo', label: 'Africa/Cairo (EET)' },
  { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (SAST)' },
];

const initialCompetitionForm = {
  name: '', location: '', start_date: '', end_date: '', pool_type: '',
  lanes: '', pool_length: '', pool_width: '', description: '',
  federation: 'Other', max_freedivers: '', organizer: '',
  contact_name: '', contact_email: '', contact_phone: '', contact_address: '',
  logo_url: '', timezone: 'UTC'
};

const CompetitionsTab = ({ t, language, competitions, aquaticInfrastructures = [], users = [], onRefresh }) => {
  const [viewMode, setViewMode] = useState('grid');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [competitionForm, setCompetitionForm] = useState(initialCompetitionForm);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);
  const [selectedInfraId, setSelectedInfraId] = useState('');
  const [zoneConfigCompetition, setZoneConfigCompetition] = useState(null);

  const handleInfrastructureSelect = (infraId) => {
    // Use 'none' sentinel value for clearing selection
    const effectiveId = infraId === 'none' ? '' : infraId;
    setSelectedInfraId(effectiveId);
    if (!effectiveId) return;
    const infra = aquaticInfrastructures.find(i => i.id === effectiveId);
    if (!infra) return;
    setCompetitionForm(prev => ({
      ...prev,
      pool_type: infra.pool_type || prev.pool_type,
      pool_length: infra.pool_length ? infra.pool_length.toString() : prev.pool_length,
      lanes: infra.number_of_lanes ? infra.number_of_lanes.toString() : prev.lanes,
      location: infra.city ? `${infra.long_name || infra.short_name}, ${infra.city}` : prev.location,
    }));
  };

  const handleCreateCompetition = async (e) => {
    e.preventDefault();
    try {
      const competitionData = {
        ...competitionForm,
        lanes: parseInt(competitionForm.lanes),
        pool_length: competitionForm.pool_length ? parseInt(competitionForm.pool_length) : null,
        pool_width: competitionForm.pool_width ? parseInt(competitionForm.pool_width) : null,
        max_freedivers: competitionForm.max_freedivers ? parseInt(competitionForm.max_freedivers) : null,
        start_date: new Date(competitionForm.start_date).toISOString(),
        end_date: new Date(competitionForm.end_date).toISOString()
      };

      await axios.post(`${API_BASE_URL}/api/competitions`, competitionData);
      setShowCreateModal(false);
      setCompetitionForm(initialCompetitionForm);
      onRefresh();
    } catch (error) {
      console.error('Error creating competition:', error);
      if (error.response?.status === 400) {
        alert(error.response.data.detail);
      } else {
        alert('Error creating competition. Please try again.');
      }
    }
  };

  const handleUpdateCompetition = async (e) => {
    e.preventDefault();
    try {
      const competitionData = {
        ...competitionForm,
        lanes: parseInt(competitionForm.lanes),
        pool_length: competitionForm.pool_length ? parseInt(competitionForm.pool_length) : null,
        pool_width: competitionForm.pool_width ? parseInt(competitionForm.pool_width) : null,
        max_freedivers: competitionForm.max_freedivers ? parseInt(competitionForm.max_freedivers) : null,
        start_date: new Date(competitionForm.start_date).toISOString(),
        end_date: new Date(competitionForm.end_date).toISOString(),
        logo_url: competitionForm.logo_url || null,
        timezone: competitionForm.timezone || 'UTC'
      };

      await axios.put(`${API_BASE_URL}/api/competitions/${selectedCompetition.id}`, competitionData);
      setShowEditModal(false);
      setSelectedCompetition(null);
      setCompetitionForm(initialCompetitionForm);
      setLogoPreview(null);
      onRefresh();
    } catch (error) {
      console.error('Error updating competition:', error);
      alert('Error updating competition. Please try again.');
    }
  };

  const handleDeleteCompetition = async (competitionId) => {
    if (window.confirm(t('confirmDeleteCompetition') || 'Are you sure you want to delete this competition?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/competitions/${competitionId}`);
        onRefresh();
      } catch (error) {
        console.error('Error deleting competition:', error);
        alert('Error deleting competition. Please try again.');
      }
    }
  };

  const handleDuplicateCompetition = async (competition) => {
    const defaultName = `${competition.name} (${t('copyShort') || 'copie'})`;
    const newName = window.prompt(t('duplicateCompetitionPrompt') || 'New competition name:', defaultName);
    if (!newName || !newName.trim()) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/api/competitions/${competition.id}/duplicate`, { new_name: newName.trim() });
      const newId = res.data?.competition?.id;
      const newDates = res.data?.competition;
      const dateStr = newDates?.start_date ? new Date(newDates.start_date).toLocaleDateString() : '';
      alert(`${t('competitionDuplicated') || 'Competition duplicated'}: ${res.data?.competition?.name}${dateStr ? ` (${dateStr})` : ''}`);
      if (onRefresh) onRefresh();
      // Optional: scroll to / highlight new competition
      if (newId) {
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="competition-card-${newId}"], [data-testid="competition-row-${newId}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    } catch (error) {
      console.error('Error duplicating competition:', error);
      alert((t('duplicateFailed') || 'Failed to duplicate') + ': ' + (error.response?.data?.detail || error.message));
    }
  };

  const openEditModal = (competition) => {
    setSelectedCompetition(competition);
    setSelectedInfraId(''); // Reset infra selection on edit
    setCompetitionForm({
      name: competition.name,
      location: competition.location,
      start_date: competition.start_date?.slice(0, 16) || '',
      end_date: competition.end_date?.slice(0, 16) || '',
      pool_type: competition.pool_type,
      lanes: competition.lanes?.toString() || '',
      pool_length: competition.pool_length?.toString() || '',
      pool_width: competition.pool_width?.toString() || '',
      description: competition.description || '',
      federation: competition.federation || 'Other',
      max_freedivers: competition.max_freedivers?.toString() || '',
      organizer: competition.organizer || '',
      contact_name: competition.contact_name || '',
      contact_email: competition.contact_email || '',
      contact_phone: competition.contact_phone || '',
      contact_address: competition.contact_address || '',
      logo_url: competition.logo_url || '',
      timezone: competition.timezone || 'UTC'
    });
    setLogoPreview(competition.logo_url || null);
    setShowEditModal(true);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Logo file must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setCompetitionForm({...competitionForm, logo_url: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setCompetitionForm({...competitionForm, logo_url: ''});
  };

  const renderCompetitionForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-blue-200">{t('competitionName')}</Label>
          <Input
            value={competitionForm.name}
            onChange={(e) => setCompetitionForm({...competitionForm, name: e.target.value})}
            className="bg-blue-800/50 border-blue-600 text-white"
            placeholder="e.g., World Freediving Championship"
            required
          />
        </div>
        <div>
          <Label className="text-blue-200">{t('location')}</Label>
          <Input
            value={competitionForm.location}
            onChange={(e) => setCompetitionForm({...competitionForm, location: e.target.value})}
            className="bg-blue-800/50 border-blue-600 text-white"
            placeholder="e.g., Miami, FL"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-blue-200">{t('startDate')}</Label>
          <Input
            type="datetime-local"
            value={competitionForm.start_date}
            onChange={(e) => setCompetitionForm({...competitionForm, start_date: e.target.value})}
            min={new Date().toISOString().slice(0, 16)}
            className="bg-blue-800/50 border-blue-600 text-white"
            required
          />
        </div>
        <div>
          <Label className="text-blue-200">{t('endDate')}</Label>
          <Input
            type="datetime-local"
            value={competitionForm.end_date}
            onChange={(e) => setCompetitionForm({...competitionForm, end_date: e.target.value})}
            min={competitionForm.start_date || new Date().toISOString().slice(0, 16)}
            className="bg-blue-800/50 border-blue-600 text-white"
            required
          />
        </div>
      </div>

      {/* Aquatic Infrastructure Selection */}
      {aquaticInfrastructures.length > 0 && (
        <div className="bg-blue-800/20 border border-blue-700/40 rounded-lg p-3">
          <Label className="text-teal-300 text-sm font-semibold block mb-1">
            {t('selectAquaticInfrastructure') || 'Select Aquatic Infrastructure (optional)'}
          </Label>
          <Select value={selectedInfraId || 'none'} onValueChange={handleInfrastructureSelect}>
            <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white" data-testid="infra-dropdown">
              <SelectValue placeholder={t('selectInfrastructurePlaceholder') || 'Choose a pool to auto-fill fields...'} />
            </SelectTrigger>
            <SelectContent className="bg-blue-800 border-blue-600">
              <SelectItem value="none">— {t('none') || 'None (manual entry)'} —</SelectItem>
              {aquaticInfrastructures.map(infra => (
                <SelectItem key={infra.id} value={infra.id}>
                  {infra.short_name} – {infra.city} ({infra.pool_type}, {infra.pool_length}m, {infra.number_of_lanes} lanes)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedInfraId && (
            <p className="text-xs text-teal-400 mt-1">
              Pool type, length, and lane count are auto-filled and locked from infrastructure.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-blue-200">{t('poolType')}</Label>
          <Select value={competitionForm.pool_type} onValueChange={(value) => !selectedInfraId && setCompetitionForm({...competitionForm, pool_type: value})} disabled={!!selectedInfraId}>
            <SelectTrigger className={`border-blue-600 text-white ${selectedInfraId ? 'bg-blue-900/30 opacity-60 cursor-not-allowed' : 'bg-blue-800/50'}`}>
              <SelectValue placeholder={t('selectPoolType')} />
            </SelectTrigger>
            <SelectContent className="bg-blue-800 border-blue-600">
              <SelectItem value="25m">25m Pool</SelectItem>
              <SelectItem value="50m">50m Pool</SelectItem>
              <SelectItem value="Open Water">Open Water</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-blue-200">{t('numberOfLanes')}</Label>
          <Input
            type="number"
            value={competitionForm.lanes}
            onChange={(e) => !selectedInfraId && setCompetitionForm({...competitionForm, lanes: e.target.value})}
            className={`border-blue-600 text-white ${selectedInfraId ? 'bg-blue-900/30 opacity-60 cursor-not-allowed' : 'bg-blue-800/50'}`}
            placeholder="8"
            required
            disabled={!!selectedInfraId}
          />
        </div>
        <div>
          <Label className="text-blue-200">{t('poolLength')}</Label>
          <Input
            type="number"
            value={competitionForm.pool_length}
            onChange={(e) => !selectedInfraId && setCompetitionForm({...competitionForm, pool_length: e.target.value})}
            className={`border-blue-600 text-white ${selectedInfraId ? 'bg-blue-900/30 opacity-60 cursor-not-allowed' : 'bg-blue-800/50'}`}
            placeholder="50"
            disabled={!!selectedInfraId}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-blue-200">{t('federation')}</Label>
          <Select value={competitionForm.federation} onValueChange={(value) => setCompetitionForm({...competitionForm, federation: value})}>
            <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
              <SelectValue placeholder={t('selectFederation')} />
            </SelectTrigger>
            <SelectContent className="bg-blue-800 border-blue-600">
              <SelectItem value="AIDA">AIDA</SelectItem>
              <SelectItem value="CMAS">CMAS</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-blue-200">{t('maxFreedivers')}</Label>
          <Input
            type="number"
            value={competitionForm.max_freedivers}
            onChange={(e) => setCompetitionForm({...competitionForm, max_freedivers: e.target.value})}
            className="bg-blue-800/50 border-blue-600 text-white"
            placeholder="120"
          />
        </div>
      </div>

      <div>
        <Label className="text-blue-200">{t('timezone')}</Label>
        <Select value={competitionForm.timezone} onValueChange={(value) => setCompetitionForm({...competitionForm, timezone: value})}>
          <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
            <SelectValue placeholder={t('selectTimezone')} />
          </SelectTrigger>
          <SelectContent className="bg-blue-800 border-blue-600 max-h-60">
            {TIMEZONES.map(tz => (
              <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-blue-200">{t('organizer')}</Label>
        <Input
          value={competitionForm.organizer}
          onChange={(e) => setCompetitionForm({...competitionForm, organizer: e.target.value})}
          className="bg-blue-800/50 border-blue-600 text-white"
          placeholder="e.g., Freediving Association"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-blue-200">{t('contactName')}</Label>
          <Input
            value={competitionForm.contact_name}
            onChange={(e) => setCompetitionForm({...competitionForm, contact_name: e.target.value})}
            className="bg-blue-800/50 border-blue-600 text-white"
            placeholder="John Doe"
          />
        </div>
        <div>
          <Label className="text-blue-200">{t('contactEmail')}</Label>
          <Input
            type="email"
            value={competitionForm.contact_email}
            onChange={(e) => setCompetitionForm({...competitionForm, contact_email: e.target.value})}
            className="bg-blue-800/50 border-blue-600 text-white"
            placeholder="john@example.com"
          />
        </div>
      </div>

      <div>
        <Label className="text-blue-200">{t('description')}</Label>
        <Textarea
          value={competitionForm.description}
          onChange={(e) => setCompetitionForm({...competitionForm, description: e.target.value})}
          className="bg-blue-800/50 border-blue-600 text-white"
          placeholder={t('competitionDescriptionPlaceholder')}
          rows={3}
        />
      </div>

      {/* Logo Upload */}
      <div>
        <Label className="text-blue-200">{t('competitionLogo')}</Label>
        <div className="flex items-center gap-4 mt-2">
          {logoPreview ? (
            <div className="relative">
              <img src={logoPreview} alt="Logo preview" className="w-20 h-20 object-contain rounded-lg border border-blue-600" />
              <button
                type="button"
                onClick={removeLogo}
                className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className="w-20 h-20 border-2 border-dashed border-blue-600 rounded-lg flex items-center justify-center text-blue-400">
              <Image size={24} />
            </div>
          )}
          <input
            type="file"
            ref={logoInputRef}
            accept="image/*"
            onChange={handleLogoUpload}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => logoInputRef.current?.click()}
            className="border-blue-500 text-blue-300 hover:bg-blue-700/50"
          >
            <Upload className="mr-2" size={16} />
            {t('uploadLogo')}
          </Button>
        </div>
      </div>

      <DialogFooter>
        <Button type="submit" className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700">
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-blue-200">{t('competitionsTitle')}</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center bg-blue-800/30 rounded-lg p-1">
            <Button
              onClick={() => setViewMode('grid')}
              size="sm"
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              className={viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-blue-300 hover:text-white'}
            >
              <Grid size={16} />
            </Button>
            <Button
              onClick={() => setViewMode('list')}
              size="sm"
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              className={viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-blue-300 hover:text-white'}
            >
              <List size={16} />
            </Button>
          </div>

          {/* Create Competition Dialog */}
          <Dialog open={showCreateModal} onOpenChange={(open) => { setShowCreateModal(open); if (!open) setSelectedInfraId(''); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white border-none">
                <Plus className="mr-2" size={18} />
                {t('newCompetition')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-blue-200">{t('createNewCompetition')}</DialogTitle>
                <DialogDescription className="text-blue-300">
                  {t('competitionManagementDesc')}
                </DialogDescription>
              </DialogHeader>
              {renderCompetitionForm(handleCreateCompetition, t('createCompetition'))}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Competitions Display */}
      {competitions.length === 0 ? (
        <Card className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30 backdrop-blur-sm">
          <CardContent className="text-center py-12">
            <Award size={48} className="mx-auto text-blue-400 mb-4" />
            <h3 className="text-xl font-semibold text-blue-200 mb-2">{t('noCompetitions')}</h3>
            <p className="text-blue-300 mb-4">{t('createFirstCompetition')}</p>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {competitions.map((competition) => (
            <Card 
              key={competition.id} 
              className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30 backdrop-blur-sm hover:scale-[1.02] transition-transform duration-200"
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    {competition.logo_url && (
                      <img src={competition.logo_url} alt="" className="w-12 h-12 object-contain rounded" />
                    )}
                    <div>
                      <CardTitle className="text-blue-200 text-lg">{competition.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className="bg-blue-600 text-white text-xs">{competition.federation || 'Other'}</Badge>
                        <Badge variant="outline" className="border-teal-500 text-teal-300 text-xs">{competition.pool_type}</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-blue-300 mb-4">
                  <p className="flex items-center">
                    <MapPin className="mr-2" size={14} />
                    {competition.location}
                  </p>
                  <p className="flex items-center">
                    <Calendar className="mr-2" size={14} />
                    {formatDate(competition.start_date, language)} - {formatDate(competition.end_date, language)}
                  </p>
                  <p className="flex items-center">
                    <Globe className="mr-2" size={14} />
                    {competition.timezone || 'UTC'}
                  </p>
                  {competition.max_freedivers && (
                    <p className="flex items-center">
                      <Users className="mr-2" size={14} />
                      {t('maxFreedivers')}: {competition.max_freedivers}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => openEditModal(competition)}
                    size="sm"
                    variant="outline"
                    className="flex-1 border-blue-500 text-blue-300 hover:bg-blue-700/50"
                    data-testid={`edit-competition-${competition.id}`}
                  >
                    <Pencil className="mr-1" size={14} />
                    {t('edit')}
                  </Button>
                  <Button
                    onClick={() => setZoneConfigCompetition(competition)}
                    size="sm"
                    variant="outline"
                    className="border-cyan-500 text-cyan-300 hover:bg-cyan-700/50"
                    title={t('configureZones') || 'Configure Zones'}
                    data-testid={`configure-zones-${competition.id}`}
                  >
                    <Settings size={14} />
                  </Button>
                  <Button
                    onClick={() => handleDuplicateCompetition(competition)}
                    size="sm"
                    variant="outline"
                    className="border-purple-500 text-purple-300 hover:bg-purple-700/50"
                    title={t('duplicate') || 'Duplicate'}
                    data-testid={`duplicate-competition-${competition.id}`}
                  >
                    <Copy size={14} />
                  </Button>
                  <Button
                    onClick={() => handleDeleteCompetition(competition.id)}
                    size="sm"
                    variant="outline"
                    className="border-red-500 text-red-300 hover:bg-red-700/50"
                    data-testid={`delete-competition-${competition.id}`}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="space-y-2">
          {/* List Header */}
          <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-blue-800/30 rounded-lg text-xs font-semibold text-blue-300 uppercase">
            <div className="col-span-3">{t('competitionName')}</div>
            <div className="col-span-2">{t('location')}</div>
            <div className="col-span-2">{t('dates')}</div>
            <div className="col-span-1">{t('poolType')}</div>
            <div className="col-span-1">{t('federation')}</div>
            <div className="col-span-1">{t('timezone')}</div>
            <div className="col-span-2 text-right">{t('actions')}</div>
          </div>
          
          {competitions.map((competition) => (
            <div 
              key={competition.id}
              className="grid grid-cols-12 gap-2 px-4 py-3 bg-gradient-to-r from-blue-800/30 to-teal-800/30 rounded-lg border border-blue-700/30 items-center hover:bg-blue-700/30 transition-colors"
            >
              <div className="col-span-3 flex items-center gap-2">
                {competition.logo_url && (
                  <img src={competition.logo_url} alt="" className="w-8 h-8 object-contain rounded" />
                )}
                <span className="text-blue-200 font-medium">{competition.name}</span>
              </div>
              <div className="col-span-2 text-blue-300 text-sm flex items-center">
                <MapPin className="mr-1" size={12} />
                {competition.location}
              </div>
              <div className="col-span-2 text-blue-300 text-sm">
                {formatDate(competition.start_date, language).split(' ').slice(0, 3).join(' ')}
              </div>
              <div className="col-span-1">
                <Badge variant="outline" className="border-teal-500 text-teal-300 text-xs">
                  {competition.pool_type}
                </Badge>
              </div>
              <div className="col-span-1">
                <Badge className="bg-blue-600 text-white text-xs">
                  {competition.federation || 'Other'}
                </Badge>
              </div>
              <div className="col-span-1 text-blue-400 text-xs">
                {(competition.timezone || 'UTC').split('/').pop()}
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                <Button
                  onClick={() => openEditModal(competition)}
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-blue-300 hover:text-white hover:bg-blue-700/50"
                  title={t('edit') || 'Edit'}
                >
                  <Pencil size={14} />
                </Button>
                <Button
                  onClick={() => setZoneConfigCompetition(competition)}
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-cyan-300 hover:text-white hover:bg-cyan-700/50"
                  title={t('configureZones') || 'Configure Zones'}
                  data-testid={`configure-zones-list-${competition.id}`}
                >
                  <Settings size={14} />
                </Button>
                <Button
                  onClick={() => handleDuplicateCompetition(competition)}
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-purple-300 hover:text-white hover:bg-purple-700/50"
                  title={t('duplicate') || 'Duplicate'}
                  data-testid={`duplicate-competition-list-${competition.id}`}
                >
                  <Copy size={14} />
                </Button>
                <Button
                  onClick={() => handleDeleteCompetition(competition.id)}
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-red-300 hover:text-white hover:bg-red-700/50"
                >
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Competition Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-blue-200">{t('editCompetition')}</DialogTitle>
            <DialogDescription className="text-blue-300">
              {t('updateCompetitionDetails')}
            </DialogDescription>
          </DialogHeader>
          {renderCompetitionForm(handleUpdateCompetition, t('updateCompetition'))}
        </DialogContent>
      </Dialog>

      {/* Zone Configuration Modal */}
      {zoneConfigCompetition && (
        <ZoneConfigModal
          open={true}
          competition={zoneConfigCompetition}
          users={users}
          onClose={() => setZoneConfigCompetition(null)}
          onSaved={() => { if (onRefresh) onRefresh(); }}
          t={t}
        />
      )}
    </div>
  );
};

export default CompetitionsTab;
